/** @module @liquidefy/code-editor/circuits/collaborative-editor @version 1.0.0 @license LQD Research LLC */

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import { buildHealthReport, emptyStats, recordSuccess, recordError, healthFromStats, formatStatsDetails } from './circuit-types.js';

import type {
  Position,
  Range,
  EditorResult,
  DocumentState,
  EditOperation,
  CollabParticipant,
  CollabCursor,
  WorkspaceCollabState,
  SyncConflict,
} from '../code-editor-types.js';

import { ok, err, generateId, now } from '../code-editor.js';

import { createDocument } from '../document/document.js';
import { applyEditToContent, createInsertEdit, createDeleteEdit, createReplaceEdit, transformEdit, composeEdits } from '../document/edit.js';

import { createCollabState, addParticipant as addCollabParticipant, removeParticipant as removeCollabParticipant, getParticipants, updateCursorPosition, getParticipantCursors, enableFollowMode, disableFollowMode } from '../workspace/collab.js';
import { createSyncState, queueSyncChange, resolveSyncConflict, addConflict } from '../workspace/sync.js';

import { createCollabSession, applyLocalOperation, applyRemoteOperation, transformOperations } from '../filesystem/collab.js';


// ═══════════════════════════════════════════════════════════════════════════════
// §1 CONFIG & HANDLE INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

export interface CollaborativeEditorConfig {
  readonly uri: string;
  readonly content: string;
  readonly userId: string;
  readonly userName: string;
  readonly color?: string;
}

export interface CollaborativeEditorHandle extends CircuitHandle {
  getDocument(): DocumentState;
  edit(position: Position, text: string): EditorResult<DocumentState>;
  applyRemoteEdit(operation: RemoteOperation): EditorResult<DocumentState>;
  addParticipant(id: string, name: string, color: string): void;
  removeParticipant(id: string): void;
  getParticipants(): CollabParticipant[];
  updateCursor(userId: string, position: Position): void;
  getCursors(): CollabCursor[];
  enableFollowMode(targetId: string): void;
  disableFollowMode(): void;
  resolveConflict(path: string, resolution: 'local' | 'remote' | 'manual'): void;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}

export interface RemoteOperation {
  readonly userId: string;
  readonly type: 'insert' | 'delete' | 'replace';
  readonly position: Position;
  readonly text: string;
  readonly deleteCount?: number;
  readonly timestamp: number;
  readonly version: number;
}


// ═══════════════════════════════════════════════════════════════════════════════
// §2 INTERNAL STATE
// ═══════════════════════════════════════════════════════════════════════════════

interface InternalCollabState {
  document: DocumentState;
  collab: WorkspaceCollabState;
  uri: string;
  localUserId: string;
  localVersion: number;
  pendingOps: RemoteOperation[];
  operationLog: Array<{ op: RemoteOperation; appliedAt: number }>;
  conflicts: SyncConflict[];
  followTarget: string | null;
}


// ═══════════════════════════════════════════════════════════════════════════════
// §3 PURE HELPERS — OT simulation, conflict detection
// ═══════════════════════════════════════════════════════════════════════════════

function positionToOffset(content: string, pos: Position): number {
  const lines = content.split('\n');
  let offset = 0;
  for (let i = 0; i < pos.line && i < lines.length; i++) {
    offset += lines[i]!.length + 1;
  }
  return offset + Math.min(pos.column, (lines[pos.line] ?? '').length);
}

function offsetToPosition(content: string, offset: number): Position {
  const lines = content.split('\n');
  let remaining = offset;
  for (let i = 0; i < lines.length; i++) {
    if (remaining <= lines[i]!.length) {
      return { line: i, column: remaining };
    }
    remaining -= lines[i]!.length + 1;
  }
  return { line: lines.length - 1, column: (lines[lines.length - 1] || '').length };
}

function applyOperationToContent(content: string, op: RemoteOperation): string {
  const offset = positionToOffset(content, op.position);

  switch (op.type) {
    case 'insert': {
      return content.slice(0, offset) + op.text + content.slice(offset);
    }
    case 'delete': {
      const deleteEnd = offset + (op.deleteCount ?? op.text.length);
      return content.slice(0, offset) + content.slice(deleteEnd);
    }
    case 'replace': {
      const replaceEnd = offset + (op.deleteCount ?? 0);
      return content.slice(0, offset) + op.text + content.slice(replaceEnd);
    }
    default:
      return content;
  }
}

function transformPosition(pos: Position, op: RemoteOperation, content: string): Position {
  const posOffset = positionToOffset(content, pos);
  const opOffset = positionToOffset(content, op.position);

  if (posOffset <= opOffset) return pos;

  let delta = 0;
  switch (op.type) {
    case 'insert':
      delta = op.text.length;
      break;
    case 'delete':
      delta = -(op.deleteCount ?? op.text.length);
      break;
    case 'replace':
      delta = op.text.length - (op.deleteCount ?? 0);
      break;
  }

  const newOffset = Math.max(opOffset, posOffset + delta);
  return offsetToPosition(content, newOffset);
}

function detectConflict(localOp: RemoteOperation, remoteOp: RemoteOperation, content: string): boolean {
  const localOffset = positionToOffset(content, localOp.position);
  const remoteOffset = positionToOffset(content, remoteOp.position);

  const localEnd = localOp.type === 'insert'
    ? localOffset
    : localOffset + (localOp.deleteCount ?? localOp.text.length);
  const remoteEnd = remoteOp.type === 'insert'
    ? remoteOffset
    : remoteOffset + (remoteOp.deleteCount ?? remoteOp.text.length);

  return localOffset < remoteEnd && remoteOffset < localEnd;
}

function generateParticipantColor(index: number): string {
  const colors = [
    '#4285f4', '#ea4335', '#fbbc05', '#34a853',
    '#ff6d01', '#46bdc6', '#7b1fa2', '#c2185b',
    '#00897b', '#5c6bc0', '#f4511e', '#039be5',
  ];
  return colors[index % colors.length]!;
}


// ═══════════════════════════════════════════════════════════════════════════════
// §4 FACTORY — createCollaborativeEditor
// ═══════════════════════════════════════════════════════════════════════════════

export function createCollaborativeEditor(config: CollaborativeEditorConfig): CollaborativeEditorHandle {
  let circuitStats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  // Create initial document
  const docResult = createDocument(config.content, 'typescript');
  if (!docResult.success) throw new Error(`Failed to create document: ${docResult.message}`);

  // Initialize collaboration state
  const initialCollab = createCollabState(config.uri, config.userId);

  const state: InternalCollabState = {
    document: docResult.value,
    collab: initialCollab,
    uri: config.uri,
    localUserId: config.userId,
    localVersion: 1,
    pendingOps: [],
    operationLog: [],
    conflicts: [],
    followTarget: null,
  };

  function updateDocument(newContent: string): DocumentState {
    state.localVersion++;
    const updated: DocumentState = {
      ...state.document,
      content: newContent,
      version: state.localVersion,
      isDirty: true,
      lineCount: newContent.split('\n').length,
      charCount: newContent.length,
    };
    state.document = updated;
    return updated;
  }

  function recordOperation(op: RemoteOperation): void {
    state.operationLog.push({ op, appliedAt: Date.now() });
    if (state.operationLog.length > 1000) {
      state.operationLog.splice(0, state.operationLog.length - 500);
    }
  }

  return Object.freeze({
    name: 'collaborative-editor',

    getDocument(): DocumentState {
      guardDestroyed();
      return state.document;
    },

    edit(position: Position, text: string): EditorResult<DocumentState> {
      guardDestroyed();
      try {
        const op: RemoteOperation = {
          userId: state.localUserId,
          type: 'insert',
          position,
          text,
          timestamp: Date.now(),
          version: state.localVersion,
        };

        const newContent = applyOperationToContent(state.document.content, op);
        const doc = updateDocument(newContent);

        // Track for broadcast
        state.pendingOps.push(op);
        recordOperation(op);

        circuitStats = recordSuccess(circuitStats);
        return ok(doc, 'EDIT_APPLIED', 'Local edit applied and queued for broadcast');
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('EDIT_FAILED', `Edit failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    applyRemoteEdit(operation: RemoteOperation): EditorResult<DocumentState> {
      guardDestroyed();
      try {
        // Check for conflicts with pending local ops
        for (const pending of state.pendingOps) {
          if (detectConflict(pending, operation, state.document.content)) {
            const conflict: SyncConflict = {
              path: state.uri,
              localVersion: state.localVersion,
              remoteVersion: operation.version,
            };
            state.conflicts.push(conflict);
          }
        }

        // Transform against pending operations (OT)
        let transformedOp = operation;
        for (const pending of state.pendingOps) {
          transformedOp = {
            ...transformedOp,
            position: transformPosition(transformedOp.position, pending, state.document.content),
          };
        }

        const newContent = applyOperationToContent(state.document.content, transformedOp);
        const doc = updateDocument(newContent);
        recordOperation(operation);

        circuitStats = recordSuccess(circuitStats);
        return ok(doc, 'REMOTE_EDIT_APPLIED', `Remote edit from ${operation.userId} applied`);
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('REMOTE_EDIT_FAILED', `Remote edit failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    addParticipant(id: string, name: string, color: string): void {
      guardDestroyed();
      const addResult = addCollabParticipant(state.collab, { id, name, color });
      if (addResult.success) state.collab = addResult.value;
      circuitStats = recordSuccess(circuitStats);
    },

    removeParticipant(id: string): void {
      guardDestroyed();
      state.collab = removeCollabParticipant(state.collab, id);
      circuitStats = recordSuccess(circuitStats);
    },

    getParticipants(): CollabParticipant[] {
      guardDestroyed();
      return getParticipants(state.collab);
    },

    updateCursor(userId: string, position: Position): void {
      guardDestroyed();
      state.collab = updateCursorPosition(state.collab, userId, position, state.uri);
      circuitStats = recordSuccess(circuitStats);
    },

    getCursors(): CollabCursor[] {
      guardDestroyed();
      return getParticipantCursors(state.collab);
    },

    enableFollowMode(targetId: string): void {
      guardDestroyed();
      const followResult = enableFollowMode(state.collab, targetId);
      if (followResult.success) state.collab = followResult.value;
      state.followTarget = targetId;
      circuitStats = recordSuccess(circuitStats);
    },

    disableFollowMode(): void {
      guardDestroyed();
      state.collab = disableFollowMode(state.collab);
      state.followTarget = null;
      circuitStats = recordSuccess(circuitStats);
    },

    resolveConflict(path: string, resolution: 'local' | 'remote' | 'manual'): void {
      guardDestroyed();
      const idx = state.conflicts.findIndex(c => c.path === path && !c.resolvedBy);
      if (idx >= 0) {
        state.conflicts[idx] = { ...state.conflicts[idx]!, resolvedBy: resolution };

        if (resolution === 'local') {
          // Keep local version, discard remote changes — no content change needed
          state.pendingOps = [];
        } else if (resolution === 'remote') {
          // Would need to revert to remote state — clear pending
          state.pendingOps = [];
        }
      }
      circuitStats = recordSuccess(circuitStats);
    },

    stats(): CircuitStats {
      return { ...circuitStats, uptime: Date.now() - createdAt };
    },

    health(): CircuitHealthReport {
      if (destroyed) return buildHealthReport('collaborative-editor', 'destroyed', 'Circuit destroyed', createdAt);
      const unresolvedConflicts = state.conflicts.filter(c => !c.resolvedBy).length;
      const healthStatus = unresolvedConflicts > 0 ? 'degraded' as const : healthFromStats(circuitStats);
      const details = `${formatStatsDetails(circuitStats)}, Participants: ${getParticipants(state.collab).length}, Unresolved conflicts: ${unresolvedConflicts}`;
      return buildHealthReport('collaborative-editor', healthStatus, details, createdAt);
    },

    destroy(): void {
      destroyed = true;
      state.pendingOps = [];
    },
  });
}
